import { on } from '@ngrx/store';
import {
  mergeQuotaSupplierTargets,
  removeId,
  supplierQuotaKey,
} from '@ps/survey-utils';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
} from './manage-survey.actions';
import { QUOTAS_PAGE_LIMIT } from '@ps/survey-data-access';
import { ManageSurveyState } from './manage-survey.model';

export const QUOTA_HANDLERS = [
  on(
    ManageSurveyActions.loadSurveyQuotas,
    (state, { search }): ManageSurveyState => ({
      ...state,
      loading: true,
      quotasLoading: true,
      quotaSearch: search ?? '',
      error: null,
    }),
  ),

  on(
    ManageSurveyActions.loadMoreQuotas,
    (state): ManageSurveyState =>
      // Ignore while a page is already in flight so a rapid second request
      // can't advance past — and skip — the page being fetched.
      state.quotasLoadingMore
        ? state
        : {
            ...state,
            quotaLoadedPage: state.quotaLoadedPage + 1,
            quotasLoadingMore: true,
            error: null,
          },
  ),

  on(
    ManageSurveyActions.loadAllQuotas,
    (state): ManageSurveyState => ({
      ...state,
      quotasLoadingMore: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyActions.resetSurveyQuotas,
    (state): ManageSurveyState => ({
      ...state,
      quotas: [],
      quotasLoading: false,
      quotaTotal: 0,
      quotaSearch: '',
      quotaLoadedPage: 0,
      quotasLoadingMore: false,
      loading: false,
      error: null,
      quotaLockingIds: [],
      quotaSavingIds: [],
      quotaCurrentlyOpenSavingIds: [],
      supplierQuotaSavingIds: [],
      supplierQuotaCurrentlyOpenSavingIds: [],
      quotaRenamingIds: [],
      quotaCpiSavingIds: [],
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyQuotasSuccess,
    (state, { quotas, total }): ManageSurveyState => ({
      ...state,
      quotas,
      quotasLoading: false,
      quotaTotal: total,
      quotaLoadedPage: 1,
      loading: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyQuotasFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      quotas: [],
      quotasLoading: false,
      quotaTotal: 0,
      quotaLoadedPage: 0,
      loading: false,
      error,
    }),
  ),

  on(
    ManageSurveyApiActions.updateCurrentCpiSuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      survey,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.updateChildCurrentCpiSuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      childSurvey: survey,
      childSurveyLoading: false,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadMoreQuotasSuccess,
    (state, { quotas }): ManageSurveyState => ({
      ...state,
      quotas: [...state.quotas, ...quotas],
      quotasLoadingMore: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.updateCurrentCpiFailure,
    (state): ManageSurveyState => state,
  ),

  on(
    ManageSurveyApiActions.loadMoreQuotasFailure,
    (state): ManageSurveyState => ({
      ...state,
      // Roll back the page advanced by loadMoreQuotas so a retry refetches it.
      quotaLoadedPage: state.quotaLoadedPage - 1,
      quotasLoadingMore: false,
    }),
  ),

  on(
    ManageSurveyApiActions.loadAllQuotasSuccess,
    (state, { quotas }): ManageSurveyState => ({
      ...state,
      quotas,
      // The response holds every quota, so the count is authoritative — don't
      // depend on a `total` header the unpaginated endpoint may not send.
      quotaTotal: quotas.length,
      quotaLoadedPage: Math.ceil(quotas.length / QUOTAS_PAGE_LIMIT),
      quotasLoadingMore: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadAllQuotasFailure,
    (state): ManageSurveyState => ({
      ...state,
      quotasLoadingMore: false,
    }),
  ),

  on(
    ManageSurveyActions.setQuotaLock,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaLockingIds: [...state.quotaLockingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaLockSuccess,
    (state, { psQuotaId, locked }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId ? { ...quota, locked } : quota,
      ),
      error: null,
      quotaLockingIds: removeId(state.quotaLockingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaLockFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaLockingIds: removeId(state.quotaLockingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.setQuotaCurrentTarget,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaSavingIds: [...state.quotaSavingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentTargetSuccess,
    (state, { psQuotaId, update }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? {
              ...quota,
              goal: update.goal,
              currentlyOpen: update.currentlyOpen,
              maximum: update.maximum,
              allocationPercentage: update.allocationPercentage,
              fielded: update.fielded,
              locked: update.locked,
              suppliers: mergeQuotaSupplierTargets(
                quota.suppliers,
                update.supplierTargets,
              ),
            }
          : quota,
      ),
      error: null,
      quotaSavingIds: removeId(state.quotaSavingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentTargetFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaSavingIds: removeId(state.quotaSavingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.setQuotaCurrentlyOpen,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaCurrentlyOpenSavingIds: [
        ...state.quotaCurrentlyOpenSavingIds,
        psQuotaId,
      ],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentlyOpenSuccess,
    (state, { psQuotaId, update }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? {
              ...quota,
              goal: update.goal,
              currentlyOpen: update.currentlyOpen,
              maximum: update.maximum,
              allocationPercentage: update.allocationPercentage,
              fielded: update.fielded,
              locked: update.locked,
              suppliers: mergeQuotaSupplierTargets(
                quota.suppliers,
                update.supplierTargets,
              ),
            }
          : quota,
      ),
      error: null,
      quotaCurrentlyOpenSavingIds: removeId(
        state.quotaCurrentlyOpenSavingIds,
        psQuotaId,
      ),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentlyOpenFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaCurrentlyOpenSavingIds: removeId(
        state.quotaCurrentlyOpenSavingIds,
        psQuotaId,
      ),
    }),
  ),

  on(
    ManageSurveyActions.setSupplierQuotaCurrentTarget,
    (state, { psQuotaId, supplierRefId }): ManageSurveyState => ({
      ...state,
      supplierQuotaSavingIds: [
        ...state.supplierQuotaSavingIds,
        supplierQuotaKey(psQuotaId, supplierRefId),
      ],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setSupplierQuotaCurrentTargetSuccess,
    (
      state,
      { psQuotaId, supplierRefId, currentTarget },
    ): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? {
              ...quota,
              suppliers: quota.suppliers.map((supplier) =>
                supplier.supplierRefId === supplierRefId
                  ? { ...supplier, currentTarget }
                  : supplier,
              ),
            }
          : quota,
      ),
      error: null,
      supplierQuotaSavingIds: removeId(
        state.supplierQuotaSavingIds,
        supplierQuotaKey(psQuotaId, supplierRefId),
      ),
    }),
  ),

  on(
    ManageSurveyApiActions.setSupplierQuotaCurrentTargetFailure,
    (state, { psQuotaId, supplierRefId }): ManageSurveyState => ({
      ...state,
      supplierQuotaSavingIds: removeId(
        state.supplierQuotaSavingIds,
        supplierQuotaKey(psQuotaId, supplierRefId),
      ),
    }),
  ),

  on(
    ManageSurveyActions.setSupplierQuotaCurrentlyOpen,
    (state, { psQuotaId, supplierRefId }): ManageSurveyState => ({
      ...state,
      supplierQuotaCurrentlyOpenSavingIds: [
        ...state.supplierQuotaCurrentlyOpenSavingIds,
        supplierQuotaKey(psQuotaId, supplierRefId),
      ],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setSupplierQuotaCurrentlyOpenSuccess,
    (
      state,
      { psQuotaId, supplierRefId, currentlyOpen },
    ): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? {
              ...quota,
              suppliers: quota.suppliers.map((supplier) =>
                supplier.supplierRefId === supplierRefId
                  ? { ...supplier, currentlyOpen }
                  : supplier,
              ),
            }
          : quota,
      ),
      error: null,
      supplierQuotaCurrentlyOpenSavingIds: removeId(
        state.supplierQuotaCurrentlyOpenSavingIds,
        supplierQuotaKey(psQuotaId, supplierRefId),
      ),
    }),
  ),

  on(
    ManageSurveyApiActions.setSupplierQuotaCurrentlyOpenFailure,
    (state, { psQuotaId, supplierRefId }): ManageSurveyState => ({
      ...state,
      supplierQuotaCurrentlyOpenSavingIds: removeId(
        state.supplierQuotaCurrentlyOpenSavingIds,
        supplierQuotaKey(psQuotaId, supplierRefId),
      ),
    }),
  ),

  on(
    ManageSurveyActions.renameQuota,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaRenamingIds: [...state.quotaRenamingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.renameQuotaSuccess,
    (state, { psQuotaId, label }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? { ...quota, label, hasCustomTitle: true }
          : quota,
      ),
      error: null,
      quotaRenamingIds: removeId(state.quotaRenamingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.renameQuotaFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaRenamingIds: removeId(state.quotaRenamingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.setQuotaCpi,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaCpiSavingIds: [...state.quotaCpiSavingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCpiSuccess,
    (state, { psQuotaId, cpi }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId && quota.qbp
          ? { ...quota, qbp: { ...quota.qbp, cpi } }
          : quota,
      ),
      error: null,
      quotaCpiSavingIds: removeId(state.quotaCpiSavingIds, psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCpiFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaCpiSavingIds: removeId(state.quotaCpiSavingIds, psQuotaId),
    }),
  ),
];
